/**
 * HeadlinesCard — "If you only read 5 lines, read these."
 *
 * Mounted at the top of the department sentiment report. Pulls a single
 * scannable headline for each of:
 *   1. Overall sentiment
 *   2. Top friction
 *   3. Top employee request
 *   4. Top risk
 *   5. Headline recommendation (with pillar badge if available)
 *
 * Defensively reads from `analysis` (full sentiment report) and `summary`
 * (condensed view) so it works for both freshly-generated and cached
 * reports — fields like `pillar` / `feeds_step` only appear in regenerated
 * reports, so they render conditionally.
 */
import React from 'react';
import {
    Heart,
    AlertTriangle,
    MessageSquare,
    ShieldAlert,
    Target,
    Sparkles
} from 'lucide-react';

const PILLAR_LABELS = {
    ai_automation: 'AI / Automation',
    outsourcing: 'Outsource / Offshore',
    role_design: 'Role Design'
};

const PILLAR_COLORS = {
    ai_automation: '#6366f1',
    outsourcing: '#0891b2',
    role_design: '#d97706'
};

function PillarBadge({ pillar }) {
    if (!pillar || !PILLAR_LABELS[pillar]) return null;
    return (
        <span
            className="headline-pillar-badge"
            style={{
                backgroundColor: `${PILLAR_COLORS[pillar]}1a`,
                color: PILLAR_COLORS[pillar],
                border: `1px solid ${PILLAR_COLORS[pillar]}40`
            }}
        >
            <Sparkles size={11} /> {PILLAR_LABELS[pillar]}
        </span>
    );
}

function StepBadge({ step }) {
    if (!step) return null;
    return <span className="headline-step-badge">Feeds Step {step}</span>;
}

function pickFirst(...candidates) {
    for (const c of candidates) {
        if (typeof c === 'string' && c.trim()) return c.trim();
        if (Array.isArray(c) && c.length > 0) {
            const first = c[0];
            if (typeof first === 'string' && first.trim()) return first.trim();
            if (first && typeof first === 'object') {
                return (
                    first.detail ||
                    first.description ||
                    first.recommendation ||
                    first.recommended_action ||
                    first.area ||
                    first.theme ||
                    first.title ||
                    null
                );
            }
        }
    }
    return null;
}

function pickTopRisk(risks) {
    if (!Array.isArray(risks) || risks.length === 0) return null;
    const order = { high: 0, elevated: 1, moderate: 2, low: 3 };
    const sorted = [...risks].sort(
        (a, b) =>
            (order[(a.severity || '').toLowerCase()] ?? 9) -
            (order[(b.severity || '').toLowerCase()] ?? 9)
    );
    return sorted[0];
}

function pickTopRecommendation(recs) {
    if (!Array.isArray(recs) || recs.length === 0) return null;
    const order = { immediate: 0, high: 1, medium: 2, low: 3 };
    const sorted = [...recs].sort(
        (a, b) =>
            (order[(a.priority || '').toLowerCase()] ?? 9) -
            (order[(b.priority || '').toLowerCase()] ?? 9)
    );
    return sorted[0];
}

export default function HeadlinesCard({ analysis, summary, department }) {
    if (!analysis && !summary) return null;

    // Pillar 1: sentiment
    const sentiment = pickFirst(
        summary?.quick_stats?.overall_sentiment,
        analysis?.team_sentiment?.overall_assessment,
        analysis?.team_sentiment?.morale_indicator
    );

    // Pillar 2: friction (process pain / inefficiency)
    const friction = pickFirst(
        analysis?.process_themes?.pain_points,
        analysis?.process_themes?.top_friction,
        analysis?.process_themes?.streamlining_opportunities,
        analysis?.workload_health?.primary_stressors
    );

    // Pillar 3: request (what employees are asking for)
    const request = pickFirst(
        analysis?.commonly_requested,
        analysis?.what_employees_want,
        analysis?.team_sentiment?.top_requests
    );

    // Pillar 4: risk
    const topRisk = pickTopRisk(analysis?.risk_indicators);
    const risk = topRisk
        ? topRisk.recommended_action || topRisk.risk_area || topRisk.description
        : null;
    const riskSeverity = topRisk?.severity;

    // Pillar 5: headline recommendation (with optional pillar/step badges)
    const topRec = pickTopRecommendation(analysis?.strategic_recommendations);
    const recText = topRec
        ? topRec.recommendation || topRec.business_impact
        : pickFirst(analysis?.strategic_recommendations);
    const recPillar = topRec?.pillar;
    const recStep = topRec?.feeds_step;

    // If we have nothing at all, skip the card entirely.
    if (!sentiment && !friction && !request && !risk && !recText) return null;

    return (
        <div className="headlines-card">
            <div className="headlines-card-header">
                <h3>
                    <Target size={16} /> If you only read 5 lines
                    {department ? <span className="headlines-dept"> — {department}</span> : null}
                </h3>
                <span className="headlines-card-subtitle">
                    Top-of-page summary for fast leadership review
                </span>
            </div>

            <ul className="headlines-list">
                {sentiment && (
                    <li className="headline-item">
                        <Heart size={16} className="headline-icon" />
                        <div className="headline-body">
                            <span className="headline-label">Overall sentiment</span>
                            <span className="headline-text">{sentiment}</span>
                        </div>
                    </li>
                )}

                {friction && (
                    <li className="headline-item">
                        <AlertTriangle size={16} className="headline-icon" />
                        <div className="headline-body">
                            <span className="headline-label">Top friction</span>
                            <span className="headline-text">{friction}</span>
                        </div>
                    </li>
                )}

                {request && (
                    <li className="headline-item">
                        <MessageSquare size={16} className="headline-icon" />
                        <div className="headline-body">
                            <span className="headline-label">What teams are asking for</span>
                            <span className="headline-text">{request}</span>
                        </div>
                    </li>
                )}

                {risk && (
                    <li className="headline-item">
                        <ShieldAlert size={16} className="headline-icon" />
                        <div className="headline-body">
                            <span className="headline-label">
                                Top risk
                                {riskSeverity ? (
                                    <span className={`headline-severity sev-${riskSeverity.toLowerCase()}`}>
                                        {riskSeverity}
                                    </span>
                                ) : null}
                            </span>
                            <span className="headline-text">{risk}</span>
                        </div>
                    </li>
                )}

                {recText && (
                    <li className="headline-item">
                        <Target size={16} className="headline-icon" />
                        <div className="headline-body">
                            <span className="headline-label">
                                Headline recommendation
                                <PillarBadge pillar={recPillar} />
                                <StepBadge step={recStep} />
                            </span>
                            <span className="headline-text">{recText}</span>
                        </div>
                    </li>
                )}
            </ul>
        </div>
    );
}
